import { useMemo, useRef, useState } from 'react';

import styled from '@emotion/styled';
import {
  DropdownMenu2,
  dropdownMenu2Classes,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@kc-console/common-ui';
import { useScopedLinkBuilder, useServiceAppState } from '@kc-console/context';
import { getAccountBaseUrl, useTranslation } from '@kc-console/core';
import IcPersonFill from '@kep/kcds-icons/IcPersonFill';

import { MediaQuery } from '../../../constants/media';
import { useRefinedUserMenuState } from '../../../hooks';

import type { DropdownMenu2ItemData, DropdownMenu2OptionItemData } from '@kc-console/common-ui';

const UserMenu = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'));

  const anchor = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const {
    state: { iamEndpoint },
  } = useServiceAppState();
  const { getProps } = useScopedLinkBuilder();

  const { domainName, userName, menus } = useRefinedUserMenuState();
  const menuItems: DropdownMenu2OptionItemData[] = useMemo(() => {
    return menus.map(menu => ({
      text: menu.name,
      // TODO(navigation): DropdownMenu2 anchor 지원 시 isAnchor:false 제거 → href spread
      onClick: getProps(menu.path, { isAnchor: false }).onClick,
    }));
  }, [menus, getProps]);

  const items: DropdownMenu2ItemData[] = [
    {
      type: 'section',
      title: t('layout:gnb.user.account_settings'),
      items: menuItems,
    },
    {
      type: 'section',
      title: '',
      items: [
        {
          text: t('layout:gnb.user.logout'),
          onClick: () => {
            window.location.href = `${iamEndpoint}/auth/logout?redirect_uri=${getAccountBaseUrl(
              window.location.origin,
            )}`;
          },
        },
      ],
    },
  ];

  return (
    <>
      <Tooltip message={t('layout:gnb.user.my_account')}>
        <div ref={anchor}>
          <UserMenuContainer onClick={() => setOpen(prev => !prev)}>
            <UserIconButton
              aria-expanded={open}
              aria-haspopup="menu"
              aria-label={t('layout:gnb.user.my_account')}
            >
              {!isMobile && (
                <UserInfoContainer>
                  <UserName variant={'label/medium/xs'} color="text/global/default">
                    {userName}
                  </UserName>
                  <DomainName variant={'label/regular/xs'} color="text/global/weak/default">
                    {domainName}
                  </DomainName>
                </UserInfoContainer>
              )}
              <UserIconContainer>
                <IcPersonFill />
              </UserIconContainer>
            </UserIconButton>
          </UserMenuContainer>
        </div>
      </Tooltip>
      <DropdownMenu2
        anchorEl={anchor.current}
        open={open}
        items={items}
        origin="bottom right"
        marginThreshold={0}
        onClose={() => setOpen(false)}
        css={{
          [`.${dropdownMenu2Classes.paper}`]: {
            minWidth: 248,
          },
        }}
      />
    </>
  );
};

const UserMenuContainer = styled.div`
  display: flex;
  cursor: pointer;
`;

const UserInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: start;
  height: 48px;
  padding: 6px 12px;

  ${MediaQuery.TIL_MEDIUM} {
    max-width: 200px;
    min-width: 144px;
  }

  ${MediaQuery.LARGE} {
    width: 200px;
  }
`;

const UserName = styled(Typography)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
`;

const DomainName = styled(Typography)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
`;

const UserIconButton = styled(IconButton)(({ theme }) => {
  return {
    color: '#878E9B',
    padding: 0,
    height: 48,
    borderRadius: 0,
    svg: {
      width: 20,
      height: 20,
    },
    '> span': {
      display: 'flex',
      alignItems: 'center',
    },
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: theme.color['surface/global/transparent/strong/hover'],
    },
    '&:active': {
      backgroundColor: theme.color['surface/global/transparent/strong/press'],
    },
    ...theme.mixins.focusRing('inside', { inverse: true }),
  };
});

const UserIconContainer = styled.div`
  width: 48px;
  height: 48px;
  background-color: ${({ theme }) => theme.color['coolGray/900']};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default UserMenu;
